import { DestroyRef } from '@angular/core'

export type LayeredOverlayDismissOptions = {
  /** True when a real nested overlay (e.g. WhatsApp phone) is showing — not a flow step. */
  isInnerOpen?: () => boolean
  /** Close only the nested overlay, leaving the parent dialog open. */
  closeInner?: () => void
  /** Close the parent Nebular dialog. */
  closeParent: () => void
  /** Optional Angular destroy hook to auto-unlisten. */
  destroyRef?: DestroyRef
}

/**
 * Owns Escape and CDK backdrop dismiss for a Nebular dialog so one keypress
 * closes that dialog (or a true nested overlay) without also closing the
 * wizard underneath.
 * Wallet top-up is a flow step of the same dialog, not an inner overlay.
 * @param {LayeredOverlayDismissOptions} options Close callbacks and optional inner overlay
 * @returns {() => void} Manual cleanup
 */
export function listenLayeredOverlayDismiss(options: LayeredOverlayDismissOptions): () => void {
  const dismissOneLayer = () => {
    if (options.isInnerOpen?.()) {
      options.closeInner?.()
      return
    }
    options.closeParent()
  }

  const onKeydown = (event: KeyboardEvent) => {
    if (event.key !== 'Escape' && event.code !== 'Escape') return
    // Holding Escape fires repeated keydowns; the first would pop top-up, the rest would close the dialog.
    if (event.repeat) return
    event.preventDefault()
    event.stopPropagation()
    event.stopImmediatePropagation()
    dismissOneLayer()
  }

  const onBackdropPointer = (event: Event) => {
    const target = event.target as HTMLElement | null
    if (!target?.classList?.contains('cdk-overlay-backdrop')) return
    event.preventDefault()
    event.stopPropagation()
    event.stopImmediatePropagation()
    dismissOneLayer()
  }

  document.addEventListener('keydown', onKeydown, true)
  document.addEventListener('pointerdown', onBackdropPointer, true)
  document.addEventListener('mousedown', onBackdropPointer, true)
  document.addEventListener('click', onBackdropPointer, true)

  const unlisten = () => {
    document.removeEventListener('keydown', onKeydown, true)
    document.removeEventListener('pointerdown', onBackdropPointer, true)
    document.removeEventListener('mousedown', onBackdropPointer, true)
    document.removeEventListener('click', onBackdropPointer, true)
  }

  options.destroyRef?.onDestroy(unlisten)
  return unlisten
}
